import {createContext} from 'react'

import {OrderScope} from '../types'

/**
 * Context props for order processes.
 * Provides common order data shared across all order processes (intake, change, cancellation).
 *
 * @template TProcessData - Order data type extending OrderScope
 */
export type OrderProcessContextProps<TProcessData extends OrderScope = OrderScope> = {
  /**
   * Process-specific order data containing common fields:
   * - customer (customerId, customerNumber, customerName)
   * - shippingAddress (siteId, siteName, street, city, country)
   * - orderId
   * - orgUnitId
   * - country
   */
  processData: TProcessData
}

/**
 * React context for order processes.
 * Provides access to common order data across different process types.
 * Must be used with a specific process provider (OrderIntakeProcessProvider, OrderChangeContextProvider, etc.)
 *
 * @example
 * // In a universal component used by multiple processes
 * function OrderCustomerInfo() {
 *   const { processData } = useOrderProcessContext()
 *   return <div>{processData.customer.customerName}</div>
 * }
 */
export const OrderProcessContext = createContext<OrderProcessContextProps<OrderScope> | undefined>(
  undefined
)

/**
 * @deprecated Use OrderProcessContextProps instead
 */
export type BaseOrderProcessContextProps<TProcessData extends OrderScope = OrderScope> =
  OrderProcessContextProps<TProcessData>
